import { Hash, Tag, X } from "lucide-react"
import { Badge } from "@/components/ui/Badge"
import { Button } from "@/components/ui/Button"
import type { Market } from "@/lib/useMarket"
import StockStatus from "@/pages/Inventory/components/StockStatus"
import type { QuickViewData } from "./types"
import MarketSwitcher from "./MarketSwitcher"

interface Props {
  externalId: string
  data: QuickViewData | null
  market: Market | null
  markets: Market[]
  onMarketChange: (market: Market) => void
  onClose: () => void
}

// Full name; one line with the product id, short name (when set), product group
// (id and group each behind an icon) and the Inventory Overview
// Status; then the market dropdown. Until the product loads, its external id
// stands in.
export default function PanelHeader({ externalId, data, market, markets, onMarketChange, onClose }: Props) {
  const product = data?.product

  return (
    <div className="flex shrink-0 flex-col gap-2 border-b border-border px-5 py-3">
      <div className="flex items-start justify-between gap-3">
        <h2 className="min-w-0 break-words text-base font-semibold text-foreground">{product?.name ?? externalId}</h2>
        {/* -my-1: as tall as one line of the name, so it adds no space under it. */}
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close" className="-my-1 h-8 w-8 shrink-0">
          <X size={16} />
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground">
        <span className="flex items-center gap-0.5">
          <Hash size={12} aria-label="Product ID" className="shrink-0" />
          <span className="select-all" title="Product ID">{product?.external_id ?? externalId}</span>
        </span>
        {product?.short_name && <span title="Short name">{product.short_name}</span>}
        {product && (
          <span className="flex items-center gap-0.5">
            <Tag size={12} aria-label="Group" className="shrink-0" />
            <span title="Group">Group: {product.product_group ?? "Unassigned"}</span>
          </span>
        )}
        {data && <StockStatus row={data.stock_status} className="flex items-center gap-1" />}
        {product?.discontinued && <Badge variant="outline">Discontinued</Badge>}
      </div>
      <div>
        <MarketSwitcher market={market} markets={markets} onChange={onMarketChange} />
      </div>
    </div>
  )
}
